<template>
  <section class="gm-panel gm-panel--flush build-detail">
    <header class="build-detail__head" :class="`path-${pathSlug}`">
      <div class="build-detail__portrait">
        <img
          v-if="archetype.icon"
          class="build-detail__art"
          :src="archetype.icon"
          :alt="archetype.name"
        />
        <span v-else class="build-detail__art build-detail__art--empty">{{ initials }}</span>
      </div>

      <div class="build-detail__identity">
        <h2 class="build-detail__name">{{ archetype.name }}</h2>
        <MetatypeBadge
          size="md"
          :metatype="profile.verdict.metatype"
          :runner-up="profile.verdict.runner_up"
          :hybrid="profile.verdict.is_hybrid"
        />
        <p class="build-detail__path">{{ shortPath }} · {{ profile.rank_name }}</p>
      </div>

      <!-- Fourteen numbers, five to a row. They were a 4-up and a 3×2 stacked
           down a 27rem column before, which is what forced the scroll they now
           avoid: at full width they all sit above the fold. -->
      <div class="build-detail__chips">
        <div
          v-for="chip in chips"
          :key="chip.label"
          class="chip"
          :class="[`is-${chip.position}`, { 'is-resource': chip.resource }]"
          :title="chip.hint"
        >
          <span class="chip__label">{{ chip.label }}</span>
          <span class="chip__value">{{ chip.value }}</span>
          <span v-if="chip.median !== null && chip.median !== undefined" class="chip__median">
            med {{ chip.median }}
          </span>
        </div>
      </div>
    </header>

    <div class="build-detail__grid">
      <aside class="build-detail__rail gm-scroll">
        <section class="rail-block">
          <h3 class="section-label">What it does with a turn</h3>
          <div v-for="(weight, name) in sortedWeights" :key="name" class="mix-row">
            <span class="mix-row__name">{{ name }}</span>
            <span class="mix-row__bar">
              <span
                class="mix-row__fill"
                :class="`fill-${name.toLowerCase()}`"
                :style="{ width: `${weight * 100}%` }"
              ></span>
            </span>
            <span class="mix-row__value">{{ Math.round(weight * 100) }}%</span>
          </div>
        </section>

        <section class="rail-block">
          <h3 class="section-label">School progression</h3>
          <div
            v-for="school in orderedSchools"
            :key="school.school_id"
            class="school"
            :class="{ 'is-open': school.is_open, 'is-conflict': school.path_conflict }"
          >
            <img v-if="school.icon" :src="school.icon" alt="" class="school__icon" />
            <div class="school__body">
              <span class="school__name">{{ school.name }}</span>
              <span class="school__meta">
                {{ school.tier }} · G{{ school.grade }}
                <template v-if="school.is_open">· {{ school.skills_open }}/{{ school.skills_total }} castable</template>
                <template v-else>· not yet</template>
              </span>
              <span v-if="school.path_conflict" class="school__conflict">Off this Path</span>
            </div>
          </div>
        </section>
      </aside>

      <div class="build-detail__skills">
        <h3 class="section-label section-label--skills">
          Castable now
          <span class="section-label__note">
            {{ profile.skills.length }} skills · best {{ round(profile.rollup.best_power_per_ap) }} per AP
          </span>
        </h3>
        <div class="build-detail__table gm-scroll">
          <SkillPowerTable :groups="skillGroups" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';
import MetatypeBadge from './MetatypeBadge.vue';
import SkillPowerTable from './SkillPowerTable.vue';
import archetypeService from '@/services/ArchetypeService.js';

const props = defineProps({
  archetype: { type: Object, required: true },
  profile: { type: Object, required: true },
  band: { type: Object, default: null },
});

const initials = computed(() =>
  props.archetype.name
    .split(/[\s-]+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join(''),
);

const shortPath = computed(() => (props.archetype.path ?? '').replace('Path of ', ''));
const pathSlug = computed(() => shortPath.value.toLowerCase() || 'none');

const skillGroups = computed(() => archetypeService.skillsBySchool(props.profile));

const sortedWeights = computed(() =>
  Object.fromEntries(
    Object.entries(props.profile.verdict.weights)
      .filter(([, weight]) => weight > 0.001)
      .sort((a, b) => b[1] - a[1]),
  ),
);

const orderedSchools = computed(() =>
  [...props.profile.schools].sort((a, b) => b.grade - a.grade),
);

const chips = computed(() => {
  const { resources, rollup } = props.profile;
  const band = props.band;
  return [
    { label: 'HP', value: resources.hp, resource: true, position: 'even', median: band ? round(band.median_hp) : null },
    { label: 'Energy', value: resources.energy, resource: true, position: 'even', median: null },
    { label: 'AP', value: resources.ap, resource: true, position: 'even', median: null },
    { label: 'Stat budget', value: props.profile.total_stats, resource: true, position: 'even', median: null },
    {
      label: 'Damage / turn',
      value: round(rollup.single_target_dpt),
      median: band ? round(band.median_single_target_dpt) : null,
      position: archetypeService.bandPosition(rollup.single_target_dpt, band?.median_single_target_dpt),
      hint: 'Best attack, against one body, repeated for a turn of action points',
    },
    {
      label: 'Turns to fall',
      value: round(rollup.turns_to_fall),
      median: band ? round(band.median_turns_to_fall) : null,
      position: invert(archetypeService.bandPosition(rollup.turns_to_fall, band?.median_turns_to_fall)),
      hint: 'How long it survives an opponent dealing damage at its own rate',
    },
    {
      label: 'Burst',
      value: round(rollup.burst_power),
      position: 'even',
      median: null,
      hint: rollup.burst_skill ? `Strongest single cast: ${rollup.burst_skill}` : '',
    },
    {
      label: 'Reach',
      value: round(rollup.engagement_reach),
      position: 'even',
      median: null,
      hint: 'Median reach of its attacks, in cells. Every cell short of the enemy costs an action point to cross',
    },
    {
      label: 'Targets hit',
      value: round(rollup.effective_targets),
      position: 'even',
      median: null,
      hint: 'Bodies its widest pattern is credited with — 1 is single-target',
    },
    {
      label: 'Sustain',
      value: round(rollup.sustain_turns),
      position: 'even',
      median: null,
      hint: 'Turns the reserve lasts while casting its main attack',
    },
    {
      label: 'Health / turn',
      value: round(rollup.health_per_turn),
      position: rollup.health_per_turn ? 'high' : 'even',
      median: null,
      hint: 'Most health a full turn of casting can cost the caster itself',
    },
    {
      label: 'Healing / turn',
      value: round(rollup.healing_per_turn),
      position: 'even',
      median: null,
      hint: 'Best heal, repeated for a turn of action points',
    },
    {
      label: 'Mitigation / turn',
      value: round(rollup.mitigation_per_turn),
      position: 'even',
      median: null,
      hint: 'Best shield, repeated for a turn of action points',
    },
    {
      label: 'Off curve',
      value: rollup.off_curve_count,
      position: rollup.off_curve_count ? 'high' : 'even',
      median: null,
      hint: 'Castable skills whose numbers disagree with the tuning curve',
    },
  ];
});

// Surviving longer than the roster is not the same kind of outlier as hitting
// harder than it, so the colouring has to flip for the metrics where more is
// safer rather than stronger.
function invert(position) {
  if (position === 'high') return 'low';
  if (position === 'low') return 'high';
  return 'even';
}

function round(value) {
  if (value === null || value === undefined) return '—';
  return Math.round(value * 10) / 10;
}
</script>

<style scoped>
.build-detail {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.build-detail__head {
  display: flex;
  align-items: stretch;
  gap: var(--gm-gap);
  flex: 0 0 auto;
  padding: var(--gm-gap-sm) var(--gm-gap);
  background: var(--gm-surface-1);
  border-bottom: 1px solid var(--path-color, var(--gm-border-dim));
}

.build-detail__head.path-json { --path-color: var(--gm-info); }
.build-detail__head.path-john { --path-color: var(--gm-accent); }

/* The art is the fastest identifier this tool has, so it stays real art rather
   than becoming a favicon — but it is a square tile in the corner now, not an
   11rem banner that pushed every number below the fold. */
.build-detail__portrait {
  flex: 0 0 auto;
  width: 4.4rem;
  height: 4.4rem;
  overflow: hidden;
  border-radius: var(--gm-radius);
  border: 1px solid var(--path-color, var(--gm-border-dim));
}

.build-detail__art {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 12%;
}

.build-detail__art--empty {
  display: grid;
  place-items: center;
  font-family: var(--gm-font-display);
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--gm-text-dim);
  background: var(--gm-surface-sunken);
}

.build-detail__identity {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
  flex: 0 0 11rem;
  min-width: 0;
}

.build-detail__name {
  margin: 0;
  font-family: var(--gm-font-display);
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: 0.02em;
  color: var(--gm-text);
}

.build-detail__path {
  margin: 0;
  font-family: var(--gm-font-display);
  font-size: 0.56rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--path-color, var(--gm-text-dim));
}

.build-detail__chips {
  flex: 1;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: var(--gm-gap-xs);
}

.chip {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.05rem;
  padding: 0.2rem 0.4rem;
  border-radius: var(--gm-radius-sm);
  border: 1px solid var(--gm-border-dim);
  border-left: 3px solid var(--gm-border-dim);
  background: var(--gm-surface-sunken);
  min-width: 0;
}

.chip.is-resource {
  border-left-color: var(--gm-border-dim);
  background: var(--gm-surface-0);
}

.chip.is-high { border-left-color: var(--gm-danger); }
.chip.is-low { border-left-color: var(--gm-info); }

.chip__label {
  font-size: 0.52rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--gm-text-dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip__value {
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--gm-text);
  font-variant-numeric: tabular-nums;
}

.chip__median {
  font-size: 0.5rem;
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

.build-detail__grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 15.5rem minmax(0, 1fr);
  gap: 1px;
  background: var(--gm-border-dim);
}

.build-detail__rail {
  min-height: 0;
  overflow-y: auto;
  padding: var(--gm-gap);
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap);
  background: var(--gm-surface-0);
}

.build-detail__skills {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  padding: var(--gm-gap-sm) var(--gm-gap) 0;
  background: var(--gm-surface-0);
}

.build-detail__table {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: var(--gm-gap);
}

.section-label {
  display: block;
  margin: 0 0 var(--gm-gap-sm);
  font-family: var(--gm-font-display);
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--gm-text-muted);
}

.section-label--skills {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gm-gap);
  margin-bottom: var(--gm-gap-xs);
}

.section-label__note {
  font-family: var(--gm-font-body);
  font-size: 0.62rem;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  color: var(--gm-text-dim);
}

.mix-row {
  display: grid;
  grid-template-columns: 4.6rem 1fr 2.2rem;
  align-items: center;
  gap: var(--gm-gap-sm);
  margin-bottom: 0.2rem;
}

.mix-row__name {
  font-size: 0.68rem;
  color: var(--gm-text-muted);
}

.mix-row__bar {
  height: 0.4rem;
  background: var(--gm-surface-sunken);
  border-radius: 999px;
  overflow: hidden;
}

.mix-row__fill {
  display: block;
  height: 100%;
  background: var(--gm-accent);
}

.fill-attacker { background: var(--gm-danger); }
.fill-defender { background: var(--gm-info); }
.fill-healer { background: var(--gm-success); }
.fill-buffer { background: var(--gm-accent-hi); }
.fill-debuffer { background: #a888c9; }

.mix-row__value {
  font-size: 0.65rem;
  text-align: right;
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

.school {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  padding: 0.3rem;
  margin-bottom: 0.3rem;
  border-radius: var(--gm-radius-sm);
  border: 1px solid var(--gm-border-dim);
  background: var(--gm-surface-sunken);
  opacity: 0.5;
}

.school.is-open {
  opacity: 1;
  border-left: 2px solid var(--gm-accent);
}

.school.is-conflict {
  border-color: var(--gm-danger);
}

/* The emblems are painterly art on a near-black ground, not flat glyphs. Below
   about 2.25rem they collapse into a dark smudge, and the tile is what gives
   them an edge to sit against. */
.school__icon {
  flex: 0 0 auto;
  width: 2.25rem;
  height: 2.25rem;
  object-fit: cover;
  border-radius: var(--gm-radius-sm);
  border: 1px solid var(--gm-border-dim);
  background: var(--gm-surface-0);
}

.school__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.school__name {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--gm-text);
}

.school__meta {
  font-size: 0.58rem;
  color: var(--gm-text-dim);
}

.school__conflict {
  font-size: 0.58rem;
  color: var(--gm-danger);
  font-weight: 600;
}

@media (max-width: 1100px) {
  .build-detail__chips {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .build-detail__grid {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr);
  }

  .build-detail__rail {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .rail-block {
    flex: 1 0 16rem;
  }
}
</style>
